import { useEffect, useRef, useState } from "react";
import type { RunState } from "../types";
import { sfx } from "../audio";

/**
 * Minimal in-game HUD: a fullscreen toggle on the left and pause on the right.
 *
 * Fullscreen note: the standard Fullscreen API works on Android, Chrome desktop
 * and Safari macOS but NOT on iOS Safari / Chrome-on-iPad (they're both
 * WebKit-on-iOS, which doesn't expose `requestFullscreen` on the document).
 * For iPad the user should add the site to their Home Screen — the manifest
 * sets `display: fullscreen` so it opens chrome-less. The toggle is hidden
 * when the API isn't available so it doesn't tease an unusable button.
 */
export function HUD({
  state,
  onPause,
  levelIndex,
  wordsCorrect,
  wordsTotal,
  currentWord,
  survivalRoundEndsAt,
}: {
  state: RunState;
  /** Kept for API compatibility (GameView still passes it). Unused. */
  totalDistance?: number;
  onPause: () => void;
  /** Teaching level number (1..10) shown in the top-left card. */
  levelIndex?: number;
  /** How many words the player has correctly answered this run. */
  wordsCorrect?: number;
  /** Total words in this lesson (usually 5). */
  wordsTotal?: number;
  /** The English word for the currently-active gate, if known. */
  currentWord?: string;
  /** Survival mode (Level 10): wall-clock ms when the round ends.
   *  When defined, the HUD renders a big animated countdown at the top
   *  of the screen so every player sees the same time-remaining. */
  survivalRoundEndsAt?: number;
}) {
  // Live-tick the survival countdown 4×/s so digits visibly count down.
  const [, force] = useState(0);
  useEffect(() => {
    if (survivalRoundEndsAt == null) return;
    const id = window.setInterval(() => force((n) => n + 1), 250);
    return () => window.clearInterval(id);
  }, [survivalRoundEndsAt]);
  const survivalSecondsLeft = survivalRoundEndsAt != null
    ? Math.max(0, Math.ceil((survivalRoundEndsAt - Date.now()) / 1000))
    : null;
  // One-shot urgent + time-up SFX as the survival countdown crosses
  // critical thresholds. Refs guard against the 250ms re-render loop
  // firing the same clip every tick.
  const urgentFiredRef = useRef(false);
  const timeUpFiredRef = useRef(false);
  useEffect(() => {
    if (survivalSecondsLeft == null) return;
    if (!urgentFiredRef.current && survivalSecondsLeft <= 7 && survivalSecondsLeft > 0) {
      urgentFiredRef.current = true;
      sfx.timerUrgent();
    }
    if (!timeUpFiredRef.current && survivalSecondsLeft === 0) {
      timeUpFiredRef.current = true;
      sfx.timeUp();
    }
  }, [survivalSecondsLeft]);
  const [fsAvailable, setFsAvailable] = useState(false);
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
    };
    setFsAvailable(
      typeof el.requestFullscreen === "function" ||
        typeof el.webkitRequestFullscreen === "function",
    );
    const onChange = () => setIsFs(!!(document.fullscreenElement || doc.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange as EventListener);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange as EventListener);
    };
  }, []);

  const toggleFs = async () => {
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => Promise<void>;
    };
    try {
      const isFsNow = !!(document.fullscreenElement || doc.webkitFullscreenElement);
      if (isFsNow) {
        await (document.exitFullscreen?.bind(document)
          ?? doc.webkitExitFullscreen?.bind(doc))?.();
      } else {
        await (el.requestFullscreen?.bind(el)
          ?? el.webkitRequestFullscreen?.bind(el))?.();
      }
    } catch {
      /* gesture rejected (iframes, PWA mode, iOS Safari) — ignore */
    }
  };

  const total = Math.max(1, wordsTotal ?? 5);
  const done = Math.min(total, Math.max(0, wordsCorrect ?? 0));
  const pct = Math.round((done / total) * 100);

  return (
    <>
      {/* Survival round-end countdown — pinned at the top centre, big and
       *  bold so every player tracks the same time. Pulses on the final
       *  10 seconds; entrance pop is driven by `survival-countdown` keyframes. */}
      {survivalSecondsLeft != null && (
        <div
          key={survivalRoundEndsAt /* re-mount → re-trigger entrance anim */}
          className={`survival-countdown ${survivalSecondsLeft <= 10 ? "is-urgent" : ""}`}
          aria-live="polite"
        >
          <span className="survival-countdown-label">Round ends in</span>
          <strong className="survival-countdown-time">
            {Math.floor(survivalSecondsLeft / 60)}:{String(survivalSecondsLeft % 60).padStart(2, "0")}
          </strong>
        </div>
      )}

      {/* Top-left progress card */}
      {(levelIndex != null || wordsTotal) && (
        <div className="hud-progress-card" data-touch-passthrough>
          <strong className="hud-progress-level">Level {levelIndex ?? 1}</strong>
          <div
            className="hud-progress-bar"
            style={{ ["--star-x" as unknown as string]: `${Math.max(4, Math.min(96, pct))}%` }}
          >
            <i style={{ width: `${Math.max(4, pct)}%` }} />
            <span className="hud-progress-star" aria-hidden>⭐</span>
          </div>
          <small className="hud-progress-words">
            {currentWord ? <em>{currentWord}</em> : `Word ${Math.min(total, done + 1)}/${total}`}
          </small>
        </div>
      )}
      {/* Top-right buttons */}
      <div className="hud-mini hud-mini-v2">
        {fsAvailable && (
          <button
            className="hud-mini-btn hud-mini-fs"
            onPointerDown={(e) => {
              // Fire on press, not click, so the touch-zone underneath
              // never gets a chance to interpret the tap as a jump.
              // Stop propagation so the gesture is fully consumed.
              e.preventDefault();
              e.stopPropagation();
              toggleFs();
            }}
            aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
            title="Fullscreen"
            style={{ touchAction: "manipulation" }}
          >
            {isFs ? "⤡" : "⛶"}
          </button>
        )}
        <button
          className="hud-mini-btn hud-mini-pause"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPause();
          }}
          aria-label={state.paused ? "Resume" : "Pause"}
          style={{ touchAction: "manipulation" }}
        >
          {state.paused ? "▶" : "⏸"}
        </button>
      </div>
    </>
  );
}
